import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useRole, useRoleNameById } from "../../hooks/useRole";
import { usePermission } from "../../hooks/usePermission";
import { useDivision, useDivisionNameById } from "../../hooks/useDivision";
import { useCountry, useCountryNameById } from "../../hooks/useCountry";
import { useCityNameById, useCountryCities } from "../../hooks/useCity";
import { useEditUser, useToggleUserStatus } from "../../hooks/useUser";
import { toast } from "react-toastify";
import { useDivisionTeams, useTeamNameById } from "../../hooks/useTeam";
import {
  EditUsersPermission,
  BlockUsersPermission,
  UnblockUsersPermission,
} from "../../constants/permissions";
import {
  DIVISION_CONTROLLER,
  SALES_SUPERVISOR,
  SUPER_ADMIN,
  TEAM_MEMBER,
} from "../../constants/roles";

export default function UserSettings({ user }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const [values, setValues] = useState({
    first_name: user.first_name || "",
    family_name: user.family_name || "",
    email_address: user.email_address || "",
    UPI_num: user.UPI_num || "",
    phone_number: user.phone_number || "",
    specialty: user.specialty || "",
    medical_license: user.medical_license || "",
    organization: user.organization || "",
    role_id: user.role_id || "",
    division_id: user.division_id || "",
    team_id: user.team_id || "",
    country_id: user.country_id || "",
    city_id: user.city_id || "",
  });

  const { data: roleData } = useRole();
  const { data: divisionData } = useDivision();
  const { data: countryData } = useCountry();
  const { data: cityData } = useCountryCities(values?.country_id);
  const { data: teamData } = useDivisionTeams(values?.division_id);
  const { data: roleName } = useRoleNameById(values?.role_id);

  const { mutate: editUser } = useEditUser();
  const { mutate: toggleStatus } = useToggleUserStatus();

  const { data: canUpdateUser } = usePermission({
    permissionName: EditUsersPermission,
  });

  const { data: canBlockUser } = usePermission({
    permissionName: BlockUsersPermission,
  });

  const { data: canUnblockUser } = usePermission({
    permissionName: UnblockUsersPermission,
  });

  // Get names for view dialog
  const { data: userRoleName } = useRoleNameById(user.role_id);
  const { data: userDivisionName } = useDivisionNameById(user.division_id);
  const { data: userTeamName } = useTeamNameById(user.team_id);
  const { data: userCountryName } = useCountryNameById(user.country_id);
  const { data: userCityName } = useCityNameById(user.city_id);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateRules = {
    first_name: { required: true },
    family_name: { required: true },
    email_address: { required: true },
    UPI_num: { required: true },
    phone_number: { required: true },
    specialty: { required: true },
    medical_license: { required: true },
    organization: { required: true },
    role_id: { required: true },
    country_id: { required: true },
    city_id: { required: true },
    division_id: {
      required:
        roleName === DIVISION_CONTROLLER ||
        roleName === SALES_SUPERVISOR ||
        roleName === TEAM_MEMBER,
    },
    team_id: {
      required: roleName === SALES_SUPERVISOR || roleName === TEAM_MEMBER,
    },
  };

  const validateAll = () => {
    const newErrors = {};

    for (const field in validateRules) {
      if (validateRules[field].required && !values[field]) {
        newErrors[field] = "This field is required";
      }
    }
    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true); 

    try {
      // Check if form is valid
      const formErrors = validateAll();
      if (Object.keys(formErrors).length > 0) {
        toast.error("Please fill all required fields");
        setIsSaving(false);
        return;
      }

      const userData = {
        first_name: values.first_name,
        family_name: values.family_name,
        email_address: values.email_address,
        UPI_num: values.UPI_num,
        phone_number: values.phone_number,
        specialty: values.specialty,
        medical_license: values.medical_license,
        organization: values.organization,
        role_id: values.role_id,
        division_id: values.division_id,
        team_id: values.team_id,
        country_id: values.country_id,
        city_id: values.city_id,
      };

      const hasChanges = Object.keys(userData).some((key) => {
        const userValue = user[key === "email_address" ? "email_address" : key];
        return userData[key]?.toString() !== userValue?.toString();
      });

      if (!hasChanges) {
        toast.info(
          "No changes detected. Please modify the data before saving."
        );
        setIsSaving(false);
        return;
      }

      await new Promise((resolve, reject) => {
        editUser(
          {
            id: user.id,
            data: userData,
          },
          {
            onSuccess: () => {
              toast.success("User updated successfully");
              setOpenEditModal(false);
              resolve();
            },
            onError: (error) => {
              toast.error(`Failed to update user: ${error.message}`);
              reject(error);
            },
          }
        );
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = () => {
    toggleStatus(
      {
        userId: user.id,
        currentStatus: user.status,
      },
      {
        onSuccess: () => {
          toast.success(
            `User ${user.status ? "blocked" : "unblocked"} successfully`
          );
          setAnchorEl(null);
        },
        onError: (error) => {
          toast.error(
            `Failed to ${user.status ? "block" : "unblock"} user: ${
              error.message
            }`
          );
        },
      }
    );
  };

  return (
    <div>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => setOpenViewModal(true)}>
          <button
            data-track="view-user-button"
            data-module="Users"
            className="flex items-center"
          >
            <i className="fa-solid fa-eye text-blue-700 mr-2" />
            <h6 className="text-blueColor">View</h6>
          </button>
        </MenuItem>

        {canUpdateUser && (
          <MenuItem onClick={() => setOpenEditModal(true)}>
            <i className="fa-solid fa-edit text-green-700 mr-3" />
            <h6 className="text-blueColor">Edit</h6>
          </MenuItem>
        )}

        {user.status
          ? canBlockUser && (
              <MenuItem onClick={handleToggleStatus}>
                <i className="fa-solid fa-user-slash text-red-600 mr-2" />
                <h6 className="text-red-600">Block</h6>
              </MenuItem>
            )
          : canUnblockUser && (
              <MenuItem onClick={handleToggleStatus}>
                <i className="fa-solid fa-lock-open text-red-600 mr-2" />
                <h6 className="text-red-600">Unblock</h6>
              </MenuItem>
            )}
      </Menu>

      {/* View Dialog */}
      <Dialog
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-user text-xl mx-2"></i>
          <span className="text-xl font-semibold">User Details</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                First Name
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.first_name}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Family Name
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.family_name}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.email_address}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                UPI Number
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.UPI_num}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Division
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {userDivisionName || "N/A"}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Team
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {userTeamName || "N/A"}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {userCountryName || "N/A"}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {userCityName || "N/A"}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Role
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {userRoleName || "N/A"}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.phone_number}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Specialty
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.specialty}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Medical License
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.medical_license}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Organization
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                {user.organization}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Status
              </label>
              <div className="w-full p-2 border rounded bg-gray-50">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    user.status
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {user.status ? "Active" : "Blocked"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={() => setOpenViewModal(false)}
              className="px-4 py-2 bg-blueColor text-white rounded"
            >
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditModal} maxWidth="md" fullWidth>
        <DialogTitle className="bg-blueColor text-white py-4">
          <i className="fas fa-user-edit text-xl mx-2"></i>
          <span className="text-xl font-semibold">Edit User Details</span>
        </DialogTitle>
        <DialogContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                First Name
              </label>
              <input
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.first_name && (
                <p className="text-red-500">{errors.first_name}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Family Name
              </label>
              <input
                name="family_name"
                value={values.family_name}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.family_name && (
                <p className="text-red-500">{errors.family_name}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email
              </label>
              <input
                name="email_address"
                value={values.email_address}
                onChange={handleChange}
                type="email"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.email_address && (
                <p className="text-red-500">{errors.email_address}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                UPI Number
              </label>
              <input
                name="UPI_num"
                value={values.UPI_num}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.UPI_num && (
                <p className="text-red-500">{errors.UPI_num}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Division
              </label>
              <select
                name="division_id"
                value={values.division_id}
                onChange={handleChange}
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select a Division
                </option>
                {divisionData?.map((division) => (
                  <option key={division.id} value={division.id}>
                    {division.name}
                  </option>
                ))}
              </select>
              {errors.division_id && (
                <p className="text-red-500">{errors.division_id}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Team
              </label>
              <select
                name="team_id"
                value={values.team_id}
                onChange={handleChange}
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select a Team
                </option>
                {teamData?.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              {errors.team_id && (
                <p className="text-red-500">{errors.team_id}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Country
              </label>
              <select
                name="country_id"
                value={values.country_id}
                onChange={handleChange}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select
                </option>
                {countryData?.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
              {errors.country_id && (
                <p className="text-red-500">{errors.country_id}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                City
              </label>
              <select
                name="city_id"
                value={values.city_id}
                onChange={handleChange}
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select
                </option>
                {cityData?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
              {errors.city_id && (
                <p className="text-red-500">{errors.city_id}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Role
              </label>
              <select
                name="role_id"
                value={values.role_id}
                onChange={handleChange}
                className="w-full p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              >
                <option value="" hidden>
                  Select a Role
                </option>
                {roleData
                  ?.filter((role) => role.name !== SUPER_ADMIN)
                  .map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
              </select>
              {errors.role_id && (
                <p className="text-red-500">{errors.role_id}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Phone Number
              </label>
              <input
                name="phone_number"
                value={values.phone_number}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.phone_number && (
                <p className="text-red-500">{errors.phone_number}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Specialty
              </label>
              <input
                name="specialty"
                value={values.specialty}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.specialty && (
                <p className="text-red-500">{errors.specialty}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Medical License
              </label>
              <input
                name="medical_license"
                value={values.medical_license}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.medical_license && (
                <p className="text-red-500">{errors.medical_license}</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Organization
              </label>
              <input
                name="organization"
                value={values.organization}
                onChange={handleChange}
                type="text"
                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
              />
              {errors.organization && (
                <p className="text-red-500">{errors.organization}</p>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-4 mt-6 p-4">
            <button
              onClick={() => setOpenEditModal(false)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
            <button
              data-track="edit-user-button"
              data-module="Users"
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 rounded text-white flex items-center justify-center ${
                isSaving ? "bg-blueColor/60 cursor-not-allowed" : "bg-blueColor"
              }`}
            >
              {isSaving ? (
                <>
                  <i
                    className={`fa-solid fa-spinner mr-2 ${
                      isSaving ? "animate-spin" : ""
                    }`}
                  ></i>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
